export const CONSENT_STORAGE_KEY = 'koinon_consent';

export interface ConsentState {
  /** Analytics storage (GA4 cookies, audience building). The only optional signal. */
  analytics: boolean;
  /** ISO timestamp of the decision, so a stale record can be re-prompted later. */
  decidedAt: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const AD_SIGNALS = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const;

/**
 * Routes that may be measured.
 *
 * Only the public marketing page is listed. The whole marketing funnel
 * (product, workflow, pricing, FAQ) lives on this single route as anchor
 * sections, so nothing is lost by excluding every other path. Authenticated
 * dashboard routes are deliberately absent: they are usage telemetry of
 * property managers, not marketing analytics, and they sit on top of other
 * people's financial records.
 */
const MEASURABLE_PATHS: ReadonlySet<string> = new Set(['/']);

export function isMeasurablePath(pathname: string): boolean {
  return MEASURABLE_PATHS.has(pathname);
}

/**
 * Send a page view for the current route, if it is measurable and the visitor
 * granted analytics consent. No-op in every other case.
 */
export function trackPageView(pathname: string): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (!isMeasurablePath(pathname)) return;
  if (!loadConsent()?.analytics) return;
  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_location: window.location.href,
    page_title: document.title,
  });
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    // Private browsing modes and blocked third-party storage throw on access.
    return null;
  }
}

export function loadConsent(): ConsentState | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.decidedAt !== 'string') {
      return null;
    }
    return { analytics: parsed.analytics, decidedAt: parsed.decidedAt };
  } catch {
    return null;
  }
}

export function saveConsent(analytics: boolean): ConsentState {
  const state: ConsentState = { analytics, decidedAt: new Date().toISOString() };
  const store = storage();
  if (store) {
    try {
      store.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // A blocked write means the choice cannot persist; the banner re-prompts.
    }
  }
  return state;
}

export function clearConsent(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Nothing to do; the banner re-prompts on next load.
  }
}

/**
 * Push the current decision into the Google tag.
 *
 * Advertising signals stay denied permanently. Koinon runs no ad or
 * remarketing tags, so exposing them would be noise, but an explicit `denied`
 * keeps the tag from reading them as "not yet decided" if one is ever added.
 */
export function applyConsentToGtag(state: ConsentState): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: state.analytics ? 'granted' : 'denied',
    ...AD_SIGNALS,
  });
}
